import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';
import { Icons } from '../../../../shared/helpers/icons';
import { IContact } from '../../../../shared/models/contact';
import { DateUtils } from '../../../../shared/helpers/dateUtils';
import { UserDataService } from '../../../../shared/services/userData.service';
import { ContactsService } from '../../../../shared/services/contact.service';
import { ICprCertifiedStaff } from '../../../../shared/models/cprFirstAidAed';
import { CprFirstAidAedService } from '../../../../shared/services/cprFirstAidAed.service';

@Component({
  selector: 'cpr-certified-form',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, SpinnerComponent],
  templateUrl: './cpr-certified-form.component.html'
})
export class CprCertifiedFormComponent implements OnInit, AfterViewInit {

  icons = Icons;

  @Input() itemToEdit: ICprCertifiedStaff | null = null;

  @Output() onClose = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<{ type: string, message: string }>();

  @ViewChild('expirationInput') expirationInput!: ElementRef;

  currentBuildingId: string | null = null;
  entry: ICprCertifiedStaff = {};
  contactsList: IContact[] = [];
  loading: boolean = false;

  constructor(
    private userDataService: UserDataService,
    private contactsService: ContactsService,
    private cprService: CprFirstAidAedService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.entry = this.itemToEdit ? { ...this.itemToEdit } : {};
    this.loadContacts();
  }

  async ngAfterViewInit(): Promise<void> {
    const { Datepicker } = await import('flowbite-datepicker');
    if (this.expirationInput) {
      new Datepicker(this.expirationInput.nativeElement, { autohide: true, format: 'mm/dd/yyyy' });
      if (this.entry.expiration_dates) {
        this.expirationInput.nativeElement.value = DateUtils.formatDateForDisplay(this.entry.expiration_dates);
      }
      this.expirationInput.nativeElement.addEventListener('changeDate', (event: any) => {
        this.entry.expiration_dates = DateUtils.formatDateForBackend(event.target.value);
      });
    }
  }

  loadContacts(): void {
    if (!this.currentBuildingId) return;
    this.contactsService.getContacts(this.currentBuildingId).subscribe({
      next: (response) => {
        this.contactsList = (response.data || []).sort((a, b) => a.name.localeCompare(b.name));
      }
    });
  }

  get isEdit(): boolean {
    return !!this.itemToEdit?.id;
  }

  get isValid(): boolean {
    return !!this.entry.name;
  }

  closeModal(): void {
    this.onClose.emit();
  }

  save(): void {
    if (!this.currentBuildingId || !this.isValid) return;

    const payload: Partial<ICprCertifiedStaff> = {
      name: this.entry.name,
      position_location: this.entry.position_location,
      certifications_held: this.entry.certifications_held,
      expiration_dates: this.entry.expiration_dates,
    };

    this.loading = true;
    const request$ = this.isEdit
      ? this.cprService.updateCertifiedStaff(this.currentBuildingId, String(this.itemToEdit!.id), payload)
      : this.cprService.createCertifiedStaff(this.currentBuildingId, payload);

    request$.subscribe({
      next: (response) => {
        this.loading = false;
        if (!response.has_error) {
          this.onSave.emit({ type: 'success', message: this.isEdit ? 'This occurred after updating a staff member.' : 'This occurred after adding a staff member.' });
        } else {
          this.onSave.emit({ type: 'unsuccess', message: response.message });
        }
      },
      error: (err) => {
        this.loading = false;
        this.onSave.emit({ type: 'unsuccess', message: err.message });
      }
    });
  }
}
